import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ImageUpload — uploads to /api/admin/upload and returns absolute URL via onChange(url).
 * value: current image URL (full or relative)
 */
export default function ImageUpload({ value, onChange, label = "Gambar", testId = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [manualUrl, setManualUrl] = useState("");

  const previewSrc = value
    ? (value.startsWith("http") ? value : `${BACKEND_URL}${value}`)
    : null;

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran gambar maksimal 5MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Gambar berhasil diupload");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal upload gambar");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const applyManualUrl = () => {
    if (manualUrl.trim()) {
      onChange(manualUrl.trim());
      setShowUrlInput(false);
      setManualUrl("");
      toast.success("URL gambar dipasang");
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="text-xs text-[#5C5868] block">{label}</label>

      {previewSrc && (
        <div className="relative inline-block">
          <img src={previewSrc} alt="preview" className="h-32 w-auto rounded-lg border border-[#E8E5EE] object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 bg-[#EF4444] text-white rounded-full p-1 hover:bg-[#DC2626]"
            data-testid={`${testId}-remove`}
            title="Hapus gambar"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-[#E8E5EE] rounded-xl p-4 text-center hover:border-[#FF8C00] transition-colors cursor-pointer bg-white"
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testId}-input`}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-[#5C5868] text-sm py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Mengupload...
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 py-2">
            <ImageIcon className="w-6 h-6 text-[#8B8794]" />
            <span className="text-sm text-[#5C5868]">
              {previewSrc ? "Klik atau drag untuk ganti gambar" : "Klik atau drag gambar ke sini"}
            </span>
            <span className="text-xs text-[#8B8794]">JPG, PNG, WEBP — maks 5MB</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs">
        <button
          type="button"
          onClick={() => setShowUrlInput(!showUrlInput)}
          className="text-[#FF8C00] hover:underline"
          data-testid={`${testId}-toggle-url`}
        >
          {showUrlInput ? "Tutup" : "atau pakai URL gambar"}
        </button>
      </div>

      {showUrlInput && (
        <div className="flex gap-2">
          <input
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
            placeholder="https://..."
            className="input-field flex-1 text-sm"
            data-testid={`${testId}-url-input`}
          />
          <button type="button" onClick={applyManualUrl} className="btn-outline text-xs py-1.5" data-testid={`${testId}-url-apply`}>
            Pakai
          </button>
        </div>
      )}
    </div>
  );
}
